import {
  Modal,
  Text,
  Group,
  Button,
  Stack,
  Loader,
  Paper,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconMail,
  IconUser,
  IconId,
  IconArrowRight,
  IconAlertTriangle,
  IconCircleCheck,
} from "@tabler/icons-react";

interface CustomerEmailConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  accountingCustomerId: string | null | undefined;
  accountingCustomerName: string | null | undefined;
  previousEmail: string | null | undefined;
  newEmail: string;
  isLoading: boolean;
  isSuccess?: boolean;
  isConfirming?: boolean;
  accountingSoftware?: string;
}

export const CustomerEmailConfirmationModal = ({
  opened,
  onClose,
  onConfirm,
  accountingCustomerId,
  accountingCustomerName,
  previousEmail,
  newEmail,
  isLoading,
  isSuccess = false,
  isConfirming = false,
  accountingSoftware,
}: CustomerEmailConfirmationModalProps) => {
  // Success state view
  if (isSuccess) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        centered
        size="sm"
        radius="lg"
      >
        <Stack align="center" py="xl" gap="md">
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: "rgba(34, 197, 94, 0.1)" }}
          >
            <IconCircleCheck size={48} className="text-green-500" />
          </div>
          <Text size="lg" fw={600} c="green.7">
            Updated {accountingSoftware || "accounting software"} successfully
          </Text>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={20} className="text-orange-500" />
          <Text fw={600}>Confirm Email Update</Text>
        </Group>
      }
      centered
      size="lg"
      radius="lg"
    >
      {isLoading ? (
        <Stack align="center" py="xl">
          <Loader size="md" color="blue" />
          <Text size="sm" c="dimmed">
            Fetching customer details from accounting software...
          </Text>
        </Stack>
      ) : (
        <Stack gap="lg">
          <Paper
            p="sm"
            radius="md"
            style={{
              backgroundColor: "rgba(255, 237, 213, 0.5)",
              border: "1px solid rgba(251, 146, 60, 0.3)",
            }}
          >
            <Group gap="xs" wrap="nowrap">
              <IconAlertTriangle
                size={18}
                className="text-orange-500 shrink-0"
              />
              <Text size="sm" c="orange.8">
                This will permanently update the customer's email in your
                accounting software.
              </Text>
            </Group>
          </Paper>

          {/* Customer Info Card */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="lg" wrap="nowrap">
              <div className="flex-1">
                <Group gap="xs" mb={4}>
                  <IconId size={14} className="text-gray-400" />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Customer ID
                  </Text>
                </Group>
                <Text size="sm" fw={500}>
                  {accountingCustomerId || "N/A"}
                </Text>
              </div>

              <Divider orientation="vertical" />

              <div className="flex-1">
                <Group gap="xs" mb={4}>
                  <IconUser size={14} className="text-gray-400" />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Customer Name
                  </Text>
                </Group>
                <Text size="sm" fw={500}>
                  {accountingCustomerName || "N/A"}
                </Text>
              </div>
            </Group>
          </Paper>

          {/* Email Change Visualization */}
          <Paper
            p="md"
            radius="md"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              border: "1px solid #e2e8f0",
            }}
          >
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="md">
              Email Change
            </Text>

            <Group gap="md" wrap="nowrap" align="center">
              {/* Previous Email */}
              <div className="flex-1">
                <Group gap="xs" mb={6}>
                  <Badge size="xs" color="gray" variant="light">
                    Current
                  </Badge>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <IconMail size={16} className="text-gray-500 shrink-0" />
                  <Text
                    size="sm"
                    c={previousEmail ? "dark" : "dimmed"}
                    style={{
                      wordBreak: "break-all",
                      textDecoration: previousEmail ? "line-through" : "none",
                      opacity: previousEmail ? 0.7 : 1,
                    }}
                  >
                    {previousEmail || "No email set"}
                  </Text>
                </Group>
              </div>

              {/* Arrow */}
              <div className="shrink-0">
                <div
                  className="rounded-full p-2"
                  style={{ backgroundColor: "rgba(59, 130, 246, 0.1)" }}
                >
                  <IconArrowRight size={18} className="text-blue-500" />
                </div>
              </div>

              {/* New Email */}
              <div className="flex-1">
                <Group gap="xs" mb={6}>
                  <Badge size="xs" color="blue" variant="light">
                    New
                  </Badge>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <IconMail size={16} className="text-blue-500 shrink-0" />
                  <Text
                    size="sm"
                    fw={600}
                    c="blue.7"
                    style={{ wordBreak: "break-all" }}
                  >
                    {newEmail}
                  </Text>
                </Group>
              </div>
            </Group>
          </Paper>

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={onClose}
              radius="md"
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={onConfirm}
              radius="md"
              leftSection={
                isConfirming ? (
                  <Loader size={16} color="white" />
                ) : (
                  <IconMail size={16} />
                )
              }
              loading={isConfirming}
            >
              Confirm Update
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
